import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { SoftDeleteButton } from '@components/shared/soft-delete-button.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { Skeleton } from '@components/ui/skeleton';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useClipboardCopy } from '@hooks/use-clipboard-copy.hook';
import {
  useAdminAvailableParentDomainsQuery,
  useAdminDeleteDomain,
  useAdminDomainQuery,
  useAdminUpdateDomain,
  useAdminUpdateDomainStatus,
} from '@queries/admin-domains.query';
import { domainSchema } from '@schemas/domain.schema';
import { useStore } from '@tanstack/react-form';
import { safeFormatDateTime } from '@utils/date.util';
import { mapToSelectOptions } from '@utils/select.util';
import { CheckIcon, CopyIcon, EditIcon, XIcon } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import React, { Suspense, useState } from 'react';
import { AdminDomainDependencies } from './admin-domain-dependencies.component';
import { AdminDomainFormData, adminDomainFormOptions } from './admin-domain-form-options';
import { AdminDomainSubdomains } from './admin-domain-subdomains.component';

const AdminDomainModalHeaderTitle: React.FC<{ domainId: string }> = (props) => {
  const { domainId } = props;
  const { data: domain } = useAdminDomainQuery(domainId);

  return <span>Domain: {domain?.name || ''}</span>;
};

type AdminDomainModalProps = {
  domainId: string;
  isOpen: boolean;
  onClose: () => void;
};

const LoadingSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col gap-4">
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-5 w-16" />
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-6 w-20" />
            </div>
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-10" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-5 w-16" />
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="flex items-center justify-between"
              >
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-8" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="p-4 rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-9 w-40" />
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdminDomainModal: React.FC<AdminDomainModalProps> = (props) => {
  const { domainId, isOpen, onClose } = props;

  const [, setSelectedDomainId] = useQueryState('domainId', parseAsString);
  const { mutateAsync: updateDomain, isPending: isUpdating } = useAdminUpdateDomain();
  const { mutateAsync: updateDomainStatus, isPending: isUpdatingStatus } = useAdminUpdateDomainStatus();
  const { mutateAsync: deleteDomain, isPending: isDeleting } = useAdminDeleteDomain();
  const { data: parentDomainsData, isPending: isLoadingParents } = useAdminAvailableParentDomainsQuery(domainId);

  const { data: domain, isPending: isPendingDomain } = useAdminDomainQuery(domainId);

  const { handleCopy, isCopied } = useClipboardCopy();

  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isEditingSubdomains, setIsEditingSubdomains] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isBusy = isUpdating || isUpdatingStatus || isDeleting;
  const totalSubdomainDependencies =
    domain?.subdomains?.reduce((sum, subdomain) => sum + (subdomain.totalDependencies ?? 0), 0) ?? 0;

  const totalDependenciesAll = (domain?.totalDependencies ?? 0) + totalSubdomainDependencies;
  const canDelete = totalDependenciesAll === 0;

  const form = useAppForm({
    ...adminDomainFormOptions,
    defaultValues: {
      name: domain?.name ?? '',
      parentDomainId: domain?.parentDomain?.id ?? null,
      subdomains: domain?.subdomains?.map((subdomain) => subdomain.id) ?? [],
    } as AdminDomainFormData,

    validators: {
      onSubmit: domainSchema,
    },

    onSubmit: async ({ value, formApi }) => {
      await updateDomain(
        {
          dto: value,
          id: domainId,
        },
        {
          onSuccess: () => {
            successToast({ description: 'Domain updated successfully' });
            formApi.reset();
            if (isEditingDetails) {
              setIsEditingDetails(false);
            }
            if (isEditingSubdomains) {
              setIsEditingSubdomains(false);
            }
          },
          onError: (error) => {
            errorToast({ description: error.message || 'Failed to update domain' });
          },
        },
      );
    },
  });

  const hasParentDomain = useStore(form.store, (state) => !!state.values.parentDomainId);
  const hasSubdomains = useStore(form.store, (state) => !!state.values.subdomains?.length);

  // Determine display mode based on hierarchy
  // - hasSubdomains: this is a parent domain → hide parent field, show subdomains tile
  // - hasParentDomain: this is a subdomain → show parent field, hide subdomains tile
  // - neither: standalone domain → show parent field AND subdomains tile
  const isParentDomain = hasSubdomains;
  const isSubdomain = hasParentDomain;
  const isStandalone = !hasSubdomains && !hasParentDomain;

  const showParentField = isSubdomain || isStandalone;
  const showSubdomainsTile = isParentDomain || isStandalone;

  const parentOptions = mapToSelectOptions(parentDomainsData?.domains ?? []);

  const handleDelete = async () => {
    await deleteDomain(domainId, {
      onSuccess: () => {
        successToast({ description: 'Domain deleted successfully' });
        void setSelectedDomainId(null);
        onClose();
      },
      onError: (error) => errorToast({ description: error.message || 'Failed to delete domain' }),
    });
  };

  const handleUpdateDomainStatus = async (archived: boolean) => {
    await updateDomainStatus(
      { archived, id: domainId },
      {
        onSuccess: () => successToast({ description: 'Domain updated successfully' }),
        onError: (error) => errorToast({ description: error.message || 'Failed to update domain' }),
      },
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      centered
      isOpen={isOpen}
      onClose={onClose}
      header={{
        className: 'text-left',
        title: (
          <Suspense fallback="Domain">
            <AdminDomainModalHeaderTitle domainId={domainId} />
          </Suspense>
        ),
      }}
      footer={{
        leftButton: (
          <SoftDeleteButton
            archived={domain?.archived ?? false}
            canDelete={canDelete}
            disabled={isBusy}
            itemType="domain"
            onArchive={() => handleUpdateDomainStatus(true)}
            onDelete={() => setShowDeleteModal(true)}
            onUnarchive={() => handleUpdateDomainStatus(false)}
          />
        ),
      }}
      width="840px"
    >
      {isPendingDomain ? (
        <LoadingSkeleton />
      ) : domain ? (
        <>
          <div className="flex flex-col gap-4">
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-4">
                <Tile
                  className="border border-gray-200 shadow-none"
                  header={{
                    actions: isEditingDetails ? (
                      <div className="flex gap-1">
                        <IconButton
                          disabled={isBusy}
                          onClick={() => {
                            form.reset();
                            setIsEditingDetails(false);
                          }}
                          size="md"
                          icon={<XIcon size={16} />}
                          variant="secondary"
                        />

                        <IconButton
                          disabled={isBusy}
                          onClick={() => form.handleSubmit()}
                          size="md"
                          icon={<CheckIcon size={16} />}
                          variant="primary"
                        />
                      </div>
                    ) : undefined,
                    iconButton: isEditingDetails
                      ? undefined
                      : {
                          disabled: isEditingSubdomains,
                          disabledTooltip: 'Save or discard changes before editing',
                          icon: <EditIcon size={16} />,
                          onClick: () => setIsEditingDetails(true),
                        },
                    title: 'Details',
                  }}
                  hoverReveal={!isEditingDetails && !isEditingSubdomains}
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-gray-500">ID</span>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900 font-mono text-xs truncate">{domainId}</span>
                        <IconButton
                          ariaLabel="Copy ID"
                          className="border border-gray-200 shrink-0"
                          icon={isCopied('ID') ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
                          onClick={() => handleCopy('ID', domainId)}
                          size="sm"
                          variant="secondary"
                        />
                      </div>
                    </div>

                    {isEditingDetails ? (
                      <form.AppForm>
                        <div className="flex flex-col gap-4">
                          <form.AppField name="name">
                            {(field) => (
                              <field.TextField
                                autoComplete="off"
                                data-1p-ignore
                                disabled={isBusy}
                                label="Name"
                                placeholder="Enter a domain name"
                              />
                            )}
                          </form.AppField>

                          {showParentField && (
                            <form.AppField name="parentDomainId">
                              {(field) => (
                                <field.SelectPopoverField
                                  disabled={isBusy || isLoadingParents}
                                  label="Parent Domain"
                                  onClear={() => field.handleChange(null)}
                                  options={parentOptions}
                                  placeholder="Select a parent domain"
                                />
                              )}
                            </form.AppField>
                          )}
                        </div>
                      </form.AppForm>
                    ) : (
                      <>
                        {domain.parentDomain && (
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-gray-500">Parent Domain</span>
                            <Button
                              onClick={() => void setSelectedDomainId(domain.parentDomain!.id)}
                              variant="link"
                              className="sm:w-auto justify-start"
                            >
                              {domain.parentDomain.name}
                            </Button>
                          </div>
                        )}

                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Name</span>
                          <span className="text-sm text-gray-900">{domain.name}</span>
                        </div>

                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Status</span>
                          <div className="w-auto">
                            <Badge
                              size="sm"
                              className="w-auto"
                              variant={domain.archived ? 'warning' : 'success'}
                            >
                              {domain.archived ? 'Archived' : 'Available'}
                            </Badge>
                          </div>
                        </div>

                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Updated</span>
                          <span className="text-sm text-gray-900">{safeFormatDateTime(domain.updatedAt)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </Tile>
              </div>

              <div className="flex flex-col gap-4">
                <AdminDomainDependencies domain={domain} />
              </div>
            </section>

            {showSubdomainsTile && (
              <AdminDomainSubdomains
                disableEdit={isEditingDetails}
                domain={domain}
                form={form}
                isBusy={isBusy}
                isEditing={isEditingSubdomains}
                onDiscard={() => {
                  form.reset();
                  setIsEditingSubdomains(false);
                }}
                onEdit={() => setIsEditingSubdomains(true)}
                onSave={() => {
                  void form.handleSubmit();
                }}
              />
            )}
          </div>
        </>
      ) : (
        <div className="text-sm text-gray-500">No domain found.</div>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        itemName={domain ? `domain "${domain.name}"` : 'domain'}
        onConfirm={handleDelete}
        setIsOpen={setShowDeleteModal}
      />
    </Modal>
  );
};
